import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import photos from "../data/travel";

const DEG = 180 / Math.PI;
const GA = Math.PI * (3 - Math.sqrt(5)); // golden angle → even spread

// Evenly distribute the photos over a unit sphere (Fibonacci) and compute the
// rotations that make each frame face outward. Radius-independent; positions
// are expressed against the CSS var --r so they rescale for free.
const layout = photos.map((p, i) => {
  const n = photos.length;
  const uy = 1 - ((i + 0.5) / n) * 2; // 1 → -1
  const rad = Math.sqrt(Math.max(0, 1 - uy * uy));
  const theta = GA * i;
  const ux = Math.cos(theta) * rad;
  const uz = Math.sin(theta) * rad;
  return {
    ...p,
    ux,
    uy,
    uz,
    phi: Math.atan2(ux, uz) * DEG,
    chi: -Math.asin(uy) * DEG,
    w: 104 + (i % 3) * 14,
  };
});

const frameStyle = (f) => ({
  width: f.w + "px",
  transform:
    `translate(-50%,-50%) translate3d(` +
    `calc(var(--r) * ${f.ux.toFixed(4)}),` +
    `calc(var(--r) * ${f.uy.toFixed(4)}),` +
    `calc(var(--r) * ${f.uz.toFixed(4)})) ` +
    `rotateY(${f.phi.toFixed(2)}deg) rotateX(${f.chi.toFixed(2)}deg)`,
});

// Wireframe rings (great circles through the poles + latitude parallels).
const MERIDIANS = [0, 30, 60, 90, 120, 150];
const PARALLELS = [-60, -30, 0, 30, 60];

const DRAG_THRESHOLD = 6; // px of movement before a press counts as a drag
const SENS = 0.22; // degrees of rotation per pixel dragged
const IDLE = 0.02; // gentle auto-drift (deg / ~16ms) when at rest

function Travel() {
  const fieldRef = useRef(null);
  const stageRef = useRef(null);
  const frameRefs = useRef([]);
  const capRef = useRef(null);
  const capPlaceRef = useRef(null);
  const capMetaRef = useRef(null);
  const focusRef = useRef(-1);
  const movedRef = useRef(0);

  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    const computeR = () => {
      const w = window.innerWidth;
      const h = fieldRef.current
        ? fieldRef.current.clientHeight
        : window.innerHeight * 0.8;
      const R = Math.max(250, Math.min(w * 0.32, h * 0.42, 430));
      if (fieldRef.current) fieldRef.current.style.setProperty("--r", R + "px");
    };
    computeR();
    window.addEventListener("resize", computeR);

    let rotY = -28;
    let tilt = -10;
    let vx = 0;
    let vy = 0;
    let hover = 0;
    let lastFocus = -2;

    // ---- drag to spin (any direction) ----
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    const field = fieldRef.current;

    const onDown = (e) => {
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      movedRef.current = 0;
      vx = 0;
      vy = 0;
      if (field) field.style.cursor = "grabbing";
    };
    const onMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      movedRef.current += Math.abs(dx) + Math.abs(dy);
      rotY += dx * SENS;
      tilt += dy * SENS; // unbounded → can flip right over
      vx = vx * 0.5 + dx * SENS * 0.5; // smoothed velocity for the throw
      vy = vy * 0.5 + dy * SENS * 0.5;
    };
    const onUp = () => {
      dragging = false;
      if (field) field.style.cursor = "grab";
    };

    if (field) field.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onUp);

    let raf;
    let prev = performance.now();
    const loop = (t) => {
      const dt = Math.min(64, t - prev);
      prev = t;
      const stage = stageRef.current;
      if (stage) {
        const hoverT = focusRef.current >= 0 ? 1 : 0;
        hover += (hoverT - hover) * 0.08;

        if (!dragging) {
          const damp = 1 - 0.9 * hover;
          rotY += (vx + IDLE) * (dt / 16) * damp;
          tilt += vy * (dt / 16) * damp;
          vx *= 0.94;
          vy *= 0.94;
        }

        stage.style.transform =
          "rotateX(" + tilt.toFixed(2) + "deg) rotateY(" + rotY.toFixed(2) + "deg)";

        const ry = rotY / DEG;
        const tx = tilt / DEG;
        const cy = Math.cos(ry);
        const sy = Math.sin(ry);
        const cx = Math.cos(tx);
        const sx = Math.sin(tx);

        for (let i = 0; i < layout.length; i++) {
          const el = frameRefs.current[i];
          const f = layout[i];
          if (!el) continue;
          const z1 = -f.ux * sy + f.uz * cy;
          const z2 = f.uy * sx + z1 * cx; // -1 back .. 1 front
          const depth = (z2 + 1) / 2;
          if (focusRef.current === i) {
            el.style.opacity = "1";
            el.style.filter = "none";
          } else {
            el.style.opacity = (0.2 + 0.8 * depth).toFixed(3);
            el.style.filter = "blur(" + ((1 - depth) * 2.2).toFixed(2) + "px)";
          }
        }

        const cap = capRef.current;
        if (cap) {
          cap.style.opacity = hover.toFixed(3);
          if (focusRef.current >= 0 && lastFocus !== focusRef.current) {
            lastFocus = focusRef.current;
            const f = layout[focusRef.current];
            if (capPlaceRef.current) capPlaceRef.current.textContent = f.cap;
            if (capMetaRef.current)
              capMetaRef.current.textContent = f.loc + " · 35mm";
          }
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener("resize", computeR);
      if (field) field.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      cancelAnimationFrame(raf);
    };
  }, []);

  // close the lightbox on Esc
  useEffect(() => {
    if (!viewer) return;
    const onKey = (e) => {
      if (e.key === "Escape") setViewer(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewer]);

  return (
    <main className="travel-main fade-up">
      <div className="travel-head">
        <Link to="/" className="back-link">
          ← Index
        </Link>
        <div className="eyebrow">
          <span className="eyebrow-dot" />
          (03) — looks better on web
        </div>
        <h1 className="page-title">travel</h1>
      </div>

      <div className="travel-field travel-globe" ref={fieldRef}>
        <div className="globe-orb" />
        <div className="travel-stage" ref={stageRef}>
          {MERIDIANS.map((a) => (
            <div
              key={"m" + a}
              className="globe-ring globe-meridian"
              style={{
                width: "calc(var(--r) * 2)",
                height: "calc(var(--r) * 2)",
                transform: `translate(-50%,-50%) rotateY(${a}deg)`,
              }}
            />
          ))}
          {PARALLELS.map((lat) => {
            const c = Math.cos(lat / DEG);
            const s = Math.sin(lat / DEG);
            return (
              <div
                key={"p" + lat}
                className={"globe-ring globe-parallel" + (lat === 0 ? " globe-equator" : "")}
                style={{
                  width: `calc(var(--r) * ${(2 * c).toFixed(4)})`,
                  height: `calc(var(--r) * ${(2 * c).toFixed(4)})`,
                  transform: `translate(-50%,-50%) translateY(calc(var(--r) * ${(-s).toFixed(
                    4
                  )})) rotateX(90deg)`,
                }}
              />
            );
          })}
          {layout.map((frame, i) => (
            <div
              key={frame.id}
              className="travel-frame"
              ref={(el) => (frameRefs.current[i] = el)}
              onMouseEnter={() => {
                focusRef.current = i;
              }}
              onMouseLeave={() => {
                if (focusRef.current === i) focusRef.current = -1;
              }}
              onClick={() => {
                if (movedRef.current < DRAG_THRESHOLD) setViewer(frame);
              }}
              style={frameStyle(frame)}
            >
              <div className="travel-card">
                <img src={frame.img} alt={frame.cap} loading="lazy" />
              </div>
            </div>
          ))}
        </div>

        <div className="travel-caption" ref={capRef}>
          <div className="travel-caption-place" ref={capPlaceRef} />
          <div className="travel-caption-meta" ref={capMetaRef} />
        </div>
      </div>

      {viewer && (
        <div className="lightbox-backdrop" onClick={() => setViewer(null)}>
          <button
            className="lightbox-close"
            onClick={() => setViewer(null)}
            aria-label="Close"
          >
            Close ✕
          </button>
          <figure className="lightbox-fig">
            <img className="lightbox-img" src={viewer.img} alt={viewer.cap} />
            <figcaption className="lightbox-cap">
              <div className="lightbox-place">{viewer.cap}</div>
              <div className="lightbox-meta">{viewer.loc} · 35mm</div>
            </figcaption>
          </figure>
        </div>
      )}
    </main>
  );
}

export default Travel;
